import { ref, computed, onMounted, onUnmounted, watch, getCurrentInstance } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAppStore } from '../stores/appStore'
import { hasActiveModal } from './useModalBackHandler'

// Shared global state for swipe gestures to allow components to read active offsets
const isSidebarOpen = ref(false)
export const isDragging    = ref(false)
const dragOffset    = ref<number | null>(null)
const pageDragOffset = ref(0)
const gestureType = ref<'drawer' | 'page-switch' | 'tab-switch' | 'recipe-switch' | 'settings-switch' | 'admin-switch' | null>(null)
const isRecipeNavigating = ref(false)
const recipeNavDirection = ref<'next' | 'prev' | null>(null)
let recipeNavTimer: ReturnType<typeof setTimeout> | null = null
const isTabNavigating = ref(false)
const tabNavDirection = ref<'next' | 'prev' | null>(null)
const tabNavTargetId = ref<string | null>(null)
let tabNavTimer: ReturnType<typeof setTimeout> | null = null
const pendingMainTab = ref<'recipes' | 'shopping-list' | null>(null)
const dragStartTab = ref<'recipes' | 'shopping-list' | null>(null)
const mainTabProgress = ref<number | null>(null)

let dragStartProgress = 0

function getCurrentTranslateX(el: HTMLElement | null): number | null {
  if (!el) return null
  const style = window.getComputedStyle(el)
  const transform = style.transform || style.webkitTransform
  if (!transform || transform === 'none') return null

  const matrixMatch = transform.match(/^matrix\((.+)\)$/)
  if (matrixMatch) {
    const values = matrixMatch[1].split(', ')
    const val = parseFloat(values[4])
    return isNaN(val) ? null : val
  }

  const matrix3dMatch = transform.match(/^matrix3d\((.+)\)$/)
  if (matrix3dMatch) {
    const values = matrix3dMatch[1].split(', ')
    const val = parseFloat(values[12])
    return isNaN(val) ? null : val
  }

  return null
}

export function useSwipeDrawer(options?: { listen?: boolean }) {
  const hasInstance = !!getCurrentInstance()
  const shouldListen = options?.listen ?? true
  const router = hasInstance ? useRouter() : undefined
  const route = hasInstance ? useRoute() : undefined
  const store = useAppStore()

  const DRAWER_WIDTH  = 280   // px – must match .side-menu-main width
  const DRAWER_SPEED_MULTIPLIER = 4 // 10px finger move = 40px drawer move (4x factor)
  const SNAP_VELOCITY = 0.15  // px/ms – flick threshold (positive = open, negative = close)
  const SNAP_RATIO    = 0.50  // 50% threshold for snap open vs close when drag is released
  const MIN_MOVE      = 8     // px total movement before direction is evaluated
  const DIRECTION_THRESHOLD = 1.25  // |dx|/|dy| ratio – accommodates natural curved thumb arcs without rejecting swipe

  const getSortedRecipeIds = () => store.resolvedRecipeIdsList

  const getNextRecipeId = () => {
    const currentId = (route?.params?.id as string) || (router?.currentRoute?.value?.params?.id as string) || ''
    if (!currentId) return null
    const ids = getSortedRecipeIds()
    const idx = ids.indexOf(currentId)
    if (idx === -1 || ids.length <= 1) return null
    const nextIdx = (idx + 1) % ids.length
    return ids[nextIdx]
  }

  const getPrevRecipeId = () => {
    const currentId = (route?.params?.id as string) || (router?.currentRoute?.value?.params?.id as string) || ''
    if (!currentId) return null
    const ids = getSortedRecipeIds()
    const idx = ids.indexOf(currentId)
    if (idx === -1 || ids.length <= 1) return null
    const prevIdx = (idx - 1 + ids.length) % ids.length
    return ids[prevIdx]
  }

  const navigateRecipe = (direction: 'next' | 'prev'): boolean => {
    if (isRecipeNavigating.value) return false
    const targetId = direction === 'next' ? getNextRecipeId() : getPrevRecipeId()
    if (!targetId) return false

    setIgnoreNextClick()
    recipeNavDirection.value = direction
    isRecipeNavigating.value = true
    store.activeRecipeIdOverride = targetId
    isDragging.value = false
    pageDragOffset.value = 0

    if (recipeNavTimer) {
      clearTimeout(recipeNavTimer)
      recipeNavTimer = null
    }

    recipeNavTimer = setTimeout(() => {
      recipeNavTimer = null
      isRecipeNavigating.value = false
      recipeNavDirection.value = null
      store.activeRecipeIdOverride = null
      gestureType.value = null

      const activeParamId = (route?.params?.id as string) || (router?.currentRoute?.value?.params?.id as string)
      if (router && activeParamId !== targetId) {
        router.replace({ name: 'recipe', params: { id: targetId } })
      }
    }, 350)

    return true
  }

  const navigateTab = (direction: 'next' | 'prev', targetIdOverride?: string): boolean => {
    if (isTabNavigating.value) return false

    const currentPath = route?.path || (router?.currentRoute?.value?.path) || (typeof window !== 'undefined' ? window.location.pathname : '') || ''
    let targetId = targetIdOverride || null

    if (!targetId) {
      if (currentPath.includes('/recipes')) {
        const books = store.recipeBooks || []
        let idx = books.findIndex((b: any) => b.id === store.activeBookId)
        if (idx === -1 && books.length > 0) idx = 0
        const targetIdx = direction === 'next' ? idx + 1 : idx - 1
        if (targetIdx >= 0 && targetIdx < books.length) {
          targetId = books[targetIdx].id
        }
      } else if (currentPath.includes('/shopping-list')) {
        const lists = store.shoppingLists || []
        let idx = lists.findIndex((l: any) => l.id === store.activeListId)
        if (idx === -1 && lists.length > 0) idx = 0
        const targetIdx = direction === 'next' ? idx + 1 : idx - 1
        if (targetIdx >= 0 && targetIdx < lists.length) {
          targetId = lists[targetIdx].id
        }
      }
    }

    if (!targetId) return false
    if (currentPath.includes('/recipes') && targetId === store.activeBookId) return false
    if (currentPath.includes('/shopping-list') && targetId === store.activeListId) return false

    setIgnoreNextClick()
    isTabNavigating.value = true
    tabNavDirection.value = direction
    tabNavTargetId.value = targetId
    isDragging.value = false
    pageDragOffset.value = 0

    if (tabNavTimer) {
      clearTimeout(tabNavTimer)
      tabNavTimer = null
    }

    const duration = import.meta.env?.TEST ? 0 : 320

    tabNavTimer = setTimeout(() => {
      tabNavTimer = null
      if (currentPath.includes('/recipes')) {
        store.selectRecipeBook ? store.selectRecipeBook(targetId!) : (store.activeBookId = targetId!)
      } else if (currentPath.includes('/shopping-list')) {
        store.selectShoppingList ? store.selectShoppingList(targetId!) : (store.activeListId = targetId!)
      }
      isTabNavigating.value = false
      tabNavDirection.value = null
      tabNavTargetId.value = null
      gestureType.value = null
    }, duration)

    return true
  }

  let dragStartBase = 0   // translateX at gesture start
  let startX        = 0
  let startY        = 0
  let lastX         = 0
  let lastT         = 0
  let velocityX     = 0   // px/ms, positive = rightward
  let tracking      = false  // are we actively tracking this gesture?
  let directionLocked   = false  // true once we've committed to horizontal drag
  let directionRejected = false  // true if gesture was determined to be vertical

  // Resolved translateX (no drag active)
  const resolvedTranslate = () => (isSidebarOpen.value ? 0 : -DRAWER_WIDTH)

  // Only intercept ghost clicks immediately after a swipe release
  let ignoreNextClick = false
  let ignoreClickTimeout: ReturnType<typeof setTimeout> | null = null

  const setIgnoreNextClick = () => {
    ignoreNextClick = true
    if (ignoreClickTimeout) clearTimeout(ignoreClickTimeout)
    ignoreClickTimeout = setTimeout(() => {
      ignoreNextClick = false
      ignoreClickTimeout = null
    }, 350)
  }

  const onClickCapture = (e: MouseEvent) => {
    if (ignoreNextClick) {
      e.preventDefault()
      e.stopPropagation()
      ignoreNextClick = false
      if (ignoreClickTimeout) {
        clearTimeout(ignoreClickTimeout)
        ignoreClickTimeout = null
      }
    }
  }

  // Desktop / Mobile detection (reactive)
  const isDesktop = ref(window.innerWidth >= 992)
  let desktopMQ: MediaQueryList | null = null

  const handleMQChange = (e: MediaQueryListEvent) => {
    isDesktop.value = e.matches
  }

  const onTouchMoveGlobal = (e: TouchEvent) => {
    if (tracking && gestureType.value === 'drawer' && e.cancelable) {
      e.preventDefault()
    }
  }

  if (hasInstance && shouldListen) {
    onMounted(() => {
      desktopMQ = window.matchMedia('(min-width: 992px)')
      isDesktop.value = desktopMQ.matches
      desktopMQ.addEventListener('change', handleMQChange)
      window.addEventListener('click', onClickCapture, true)
      window.addEventListener('touchmove', onTouchMoveGlobal, { passive: false, capture: true })
      window.addEventListener('pointerdown', onPointerDown, { capture: true, passive: false })
      window.addEventListener('pointermove', onPointerMove, { capture: true, passive: false })
      window.addEventListener('pointerup', onPointerUp, { capture: true, passive: false })
      window.addEventListener('pointercancel', onPointerCancel, { capture: true, passive: false })
    })

    onUnmounted(() => {
      desktopMQ?.removeEventListener('change', handleMQChange)
      window.removeEventListener('click', onClickCapture, true)
      window.removeEventListener('touchmove', onTouchMoveGlobal, true)
      window.removeEventListener('pointerdown', onPointerDown, true)
      window.removeEventListener('pointermove', onPointerMove, true)
      window.removeEventListener('pointerup', onPointerUp, true)
      window.removeEventListener('pointercancel', onPointerCancel, true)
    })
  }

  // Horizontal-scroll exclusion: elements that should handle their own horizontal gestures
  const isHorizontalScrollable = (el: HTMLElement | null): boolean => {
    if (!el) return false
    const excluded = [
      '.category-filter-bar',
      '.recipes-filter-bar',
      '.recipe-book-tabs',
      '.shopping-list-tabs',
      '.toolbar',
      '.tab-bar',
      '.settings-tab-bar',
      '.admin-tabs',
      '[data-no-drawer-swipe]',
    ]
    for (const selector of excluded) {
      if (el.closest(selector)) return true
    }
    return false
  }

  const isInputOrInteractive = (el: HTMLElement | null): boolean => {
    if (!el) return false
    if (el.closest('.menu-btn, .header-btn')) return false

    // Controls that have NO horizontal drag/scroll never block swipe gestures
    if (el.closest('select, .mode-toggle, .toggle-switch, .settings-select')) {
      return false
    }

    const currentPath = route?.path || ''
    const isTabbedPage = currentPath === '/settings' || currentPath === '/index'

    // On tabbed pages (Settings & Admin), rows, buttons and toggles do not block swipe
    // unless inside an open modal overlay or a range slider
    if (isTabbedPage && !el.closest('.modal-overlay, .settings-search-modal, .settings-search-overlay, [role="dialog"], [aria-modal="true"], input[type="range"]')) {
      return false
    }

    // Recipe list rows and shopping list items should NOT block page-switching swipe gestures.
    // Only their inner action buttons (e.g. shopping cart button, delete button) should block.
    const recipeRow = el.closest('.list-item-row-recipe')
    if (recipeRow) {
      if (el.closest('.action-btn, button, input')) {
        return true
      }
      return false
    }

    const shoppingRow = el.closest('.shopping-item')
    if (shoppingRow) {
      if (el.closest('.delete-btn, .check-btn, button, input')) {
        return true
      }
      return false
    }

    const ingredientRow = el.closest('.ingredient-row')
    if (ingredientRow) {
      if (el.closest('.add-btn, button, input')) {
        return true
      }
      return false
    }

    const stepRow = el.closest('.step-row')
    if (stepRow) {
      if (el.closest('button, input, .step-timer-box')) {
        return true
      }
      return false
    }

    const tagName = el.tagName ? el.tagName.toLowerCase() : ''
    const inputType = (el as HTMLInputElement).type ? (el as HTMLInputElement).type.toLowerCase() : ''

    // Interactive form controls ALWAYS block gestures unless left-edge or inHeader
    if (
      tagName === 'button' ||
      inputType === 'checkbox' ||
      inputType === 'radio' ||
      inputType === 'range' ||
      inputType === 'color' ||
      inputType === 'file' ||
      inputType === 'submit' ||
      inputType === 'button' ||
      el.closest('.checkbox-container, .drag-handle, .sortable-chosen, .is-dragging, input[type="range"], .create-new-row, .search-overlay, .modal-content, [role="button"], [role="dialog"], [aria-modal="true"], .settings-search-overlay, .settings-search-modal, .modal-overlay, .cropper-overlay, .cropper-card, .handle')
    ) {
      return true
    }

    // Text inputs & textareas ONLY block gestures if actively focused / being edited
    const activeEl = document.activeElement
    if (el === activeEl || el.closest('input:focus, textarea:focus')) {
      return true
    }

    return false
  }

  const rejectGesture = (e: PointerEvent) => {
    directionRejected = true
    tracking = false
    isDragging.value = false
    dragOffset.value = null
    pageDragOffset.value = 0
    gestureType.value = null
    mainTabProgress.value = null

    const targetEl = e.target as HTMLElement | null
    if (targetEl && typeof targetEl.releasePointerCapture === 'function') {
      try {
        targetEl.releasePointerCapture(e.pointerId)
      } catch (err) {
        // ignore
      }
    }
  }

  const EDGE_SWIPE_THRESHOLD = 45 // px from left screen edge

  const onPointerDown = (e: PointerEvent) => {
    if (isRecipeNavigating.value || isTabNavigating.value) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    // Block all swipe gestures when any modal (edit dialog, cropper, etc.) is open
    // Ignore inline modals (such as shopping item mini-edit)
    if (hasActiveModal(false)) return

    const targetEl = e.target instanceof Element ? (e.target as HTMLElement) : null
    // If an inline edit modal/overlay is open and interaction is inside it, do not close and do not swipe
    if (targetEl?.closest?.('.inline-edit-overlay, .inline-edit-content, .modal-mini-edit')) {
      return
    }

    // Tapping on add item buttons or interactive footer elements should not trigger drawer gestures or premature resets
    if (targetEl?.closest?.('.add-item-row, .add-item-btn-main, .empty-actions, .empty-action-btn')) {
      return
    }

    // Tapping anywhere outside the active inline edit closes the inline edit mode
    if (store.activeEditingShoppingItemId) {
      if (!targetEl?.closest?.('.shopping-item.is-active-editing')) {
        store.activeEditingShoppingItemId = null
      }
    }
    const currentPath = route?.path || ''
    // Block drawer gestures on the login page
    if (currentPath === '/login') return

    startX        = e.clientX
    startY        = e.clientY
    lastX         = e.clientX
    lastT         = e.timeStamp
    velocityX     = 0
    tracking      = true
    directionLocked   = false
    directionRejected = false
    isDragging.value  = false
    dragOffset.value  = null
    pageDragOffset.value = 0
    gestureType.value = null

    const isLeftEdge = startX <= EDGE_SWIPE_THRESHOLD
    const isMenuOrHeaderBtn = !!targetEl?.closest('.menu-btn, .header-btn')
    const inHeader = !!targetEl?.closest('.app-header, .header-section, .header-row, .title-bar, .page-header, .header-titles')

    if (isMenuOrHeaderBtn) {
      tracking = false
      return
    }

    if (!isLeftEdge && !inHeader) {
      if (isHorizontalScrollable(targetEl)) {
        const scrollContainer = targetEl?.closest('.category-filter-bar, .recipes-filter-bar, .tab-bar, .toolbar, .settings-tab-bar, .admin-tabs') as HTMLElement | null
        if (scrollContainer && scrollContainer.scrollLeft > 5) {
          tracking = false
          return
        }
      }

      if (isInputOrInteractive(targetEl)) {
        tracking = false
        return
      }
    }

    if (isSidebarOpen.value && !isDesktop.value) {
      gestureType.value = 'drawer'
      dragStartBase = resolvedTranslate()
    } else if (!isDesktop.value || e.pointerType === 'touch') {
      const onMainPages = currentPath === '/recipes' || currentPath === '/shopping-list'
      if (isLeftEdge || inHeader) {
        gestureType.value = 'drawer'
        dragStartBase = resolvedTranslate()
      } else if (onMainPages) {
        gestureType.value = 'page-switch'
        dragStartTab.value = currentPath === '/recipes' ? 'recipes' : 'shopping-list'

        const sliderEl = document.querySelector('.page-main-slider') as HTMLElement | null
        const screenW = window.innerWidth || 375
        const currentTx = getCurrentTranslateX(sliderEl)

        if (currentTx !== null) {
          dragStartProgress = Math.max(0, Math.min(1, -currentTx / screenW))
        } else {
          const currentTab = pendingMainTab.value || (route?.path === '/recipes' ? 'recipes' : 'shopping-list')
          dragStartProgress = currentTab === 'shopping-list' ? 1 : 0
        }
        mainTabProgress.value = dragStartProgress
      } else if (route?.name === 'recipe') {
        gestureType.value = 'recipe-switch'
      } else if (route?.path === '/settings') {
        gestureType.value = 'settings-switch'
      } else if (route?.path === '/index') {
        gestureType.value = 'admin-switch'
      } else {
        tracking = false
        return
      }
    }
  }

  const onPointerMove = (e: PointerEvent) => {
    if (!tracking) return
    if (directionRejected) return

    const dx = e.clientX - startX
    const dy = e.clientY - startY
    const absDx = Math.abs(dx)
    const absDy = Math.abs(dy)

    if (!directionLocked) {
      const totalMove = Math.sqrt(absDx * absDx + absDy * absDy)
      if (totalMove < MIN_MOVE) return

      if (gestureType.value === 'drawer') {
        if (!isSidebarOpen.value) {
          if (dx < -10 || (absDy > 30 && absDx < 5)) {
            rejectGesture(e)
            return
          }
        }
      } else {
        // For page-switch / recipe-switch / settings-switch / admin-switch, require distinctly horizontal movement
        if (absDy > 0 && absDx / absDy < DIRECTION_THRESHOLD) {
          rejectGesture(e)
          return
        }

        // --- MAIN SWIPE MODE EVALUATION ---
        const currentPath = route?.path || (router?.currentRoute?.value?.path) || ''
        const onMainPages = currentPath === '/recipes' || currentPath === '/shopping-list'
        if (onMainPages) {
          const mode = store.mainSwipeMode || 'intelligent'

          if (mode === 'page') {
            gestureType.value = 'page-switch'
          } else if (mode === 'tab') {
            gestureType.value = 'tab-switch'
            mainTabProgress.value = null
          } else {
            // Intelligent Swipe:
            let hasMoreTabsInDirection = false

            if (currentPath === '/recipes') {
              const books = store.recipeBooks || []
              if (books.length <= 1) {
                hasMoreTabsInDirection = false
              } else {
                let idx = books.findIndex((b: any) => b.id === store.activeBookId)
                if (idx === -1) idx = 0

                if (dx < 0) {
                  // Swiping left -> next tab
                  hasMoreTabsInDirection = idx < books.length - 1
                } else if (dx > 0) {
                  // Swiping right -> prev tab
                  hasMoreTabsInDirection = idx > 0
                }
              }
            } else if (currentPath === '/shopping-list') {
              const lists = store.shoppingLists || []
              if (lists.length <= 1) {
                hasMoreTabsInDirection = false
              } else {
                let idx = lists.findIndex((l: any) => l.id === store.activeListId)
                if (idx === -1) idx = 0

                if (dx > 0) {
                  // Swiping right -> prev tab (moving towards recipes)
                  hasMoreTabsInDirection = idx > 0
                } else if (dx < 0) {
                  // Swiping left -> next tab
                  hasMoreTabsInDirection = idx < lists.length - 1
                }
              }
            }

            if (hasMoreTabsInDirection) {
              gestureType.value = 'tab-switch'
              mainTabProgress.value = null
            } else {
              const canSwitchPage = (currentPath === '/recipes' && dx < 0) || (currentPath === '/shopping-list' && dx > 0)
              if (canSwitchPage) {
                gestureType.value = 'page-switch'
              } else {
                gestureType.value = 'tab-switch'
                mainTabProgress.value = null
              }
            }
          }
        }
      }

      directionLocked = true
    }

    if (directionLocked && (isDragging.value || isSidebarOpen.value) && e.cancelable) {
      e.preventDefault()
    }

    const dt = e.timeStamp - lastT
    if (dt > 0) {
      const mult = gestureType.value === 'drawer' ? DRAWER_SPEED_MULTIPLIER : 1
      const inst = ((e.clientX - lastX) * mult) / dt
      velocityX = velocityX * 0.5 + inst * 0.5
    }
    lastX = e.clientX
    lastT = e.timeStamp

    if (gestureType.value === 'drawer') {
      isDragging.value = true
      const effectiveDx = dx * DRAWER_SPEED_MULTIPLIER
      dragOffset.value = Math.max(-DRAWER_WIDTH, Math.min(0, dragStartBase + effectiveDx))
    } else if (gestureType.value === 'page-switch') {
      isDragging.value = true
      const screenW = window.innerWidth || 375
      const dragRatio = -dx / screenW
      mainTabProgress.value = Math.max(0, Math.min(1, dragStartProgress + dragRatio))
      pageDragOffset.value = dx
    } else if (gestureType.value === 'tab-switch' || gestureType.value === 'recipe-switch' || gestureType.value === 'settings-switch' || gestureType.value === 'admin-switch') {
      isDragging.value = true
      pageDragOffset.value = dx
    }
  }

  const onPointerUp = (e: PointerEvent) => {
    if (!tracking) return
    const currentGestureType = gestureType.value
    const wasDragging = isDragging.value
    const currentDragOffset = dragOffset.value

    tracking = false

    const targetEl = e.target as HTMLElement | null
    if (targetEl && typeof targetEl.releasePointerCapture === 'function') {
      try {
        targetEl.releasePointerCapture(e.pointerId)
      } catch (err) {
        // ignore
      }
    }

    const totalMoveX = Math.abs(e.clientX - startX)
    const totalMoveY = Math.abs(e.clientY - startY)
    if (wasDragging || totalMoveX > 8 || totalMoveY > 8) {
      setIgnoreNextClick()
    }

    if (currentGestureType === 'drawer') {
      gestureType.value = null
      isDragging.value = false
      pageDragOffset.value = 0
      if (!wasDragging || currentDragOffset === null) {
        dragOffset.value = null
        return
      }

      setIgnoreNextClick()

      const progress = (currentDragOffset + DRAWER_WIDTH) / DRAWER_WIDTH
      let shouldOpen: boolean
      if (Math.abs(velocityX) >= SNAP_VELOCITY) {
        shouldOpen = velocityX > 0
      } else {
        shouldOpen = progress >= SNAP_RATIO
      }

      isSidebarOpen.value = shouldOpen
      dragOffset.value    = null
    } else if (currentGestureType === 'page-switch') {
      const currentProgress = mainTabProgress.value !== null ? mainTabProgress.value : dragStartProgress

      if (!wasDragging) {
        gestureType.value = null
        mainTabProgress.value = null
        return
      }

      let targetTab: 'recipes' | 'shopping-list'
      if (Math.abs(velocityX) >= SNAP_VELOCITY) {
        targetTab = velocityX < 0 ? 'shopping-list' : 'recipes'
      } else {
        targetTab = currentProgress >= 0.5 ? 'shopping-list' : 'recipes'
      }

      setIgnoreNextClick()
      const targetRoute = targetTab === 'recipes' ? '/recipes' : '/shopping-list'

      pendingMainTab.value = targetTab
      isDragging.value = false
      pageDragOffset.value = 0
      gestureType.value = null
      mainTabProgress.value = null

      if (route && router && route.path !== targetRoute) {
        router.replace(targetRoute)
      }
      pendingMainTab.value = null
    } else if (currentGestureType === 'tab-switch') {
      if (!wasDragging) {
        gestureType.value = null
        mainTabProgress.value = null
        return
      }

      const dx = e.clientX - startX
      const SWIPE_TAB_THRESHOLD = 35 // px
      let deltaIndex = 0
      if (dx < -SWIPE_TAB_THRESHOLD || (velocityX < -SNAP_VELOCITY && dx < -15)) {
        deltaIndex = 1
      } else if (dx > SWIPE_TAB_THRESHOLD || (velocityX > SNAP_VELOCITY && dx > 15)) {
        deltaIndex = -1
      }

      mainTabProgress.value = null

      if (deltaIndex === 1 && navigateTab('next')) {
        // Tab navigating forward
      } else if (deltaIndex === -1 && navigateTab('prev')) {
        // Tab navigating backward
      } else {
        isDragging.value = false
        pageDragOffset.value = 0
        gestureType.value = null
      }
    } else if (currentGestureType === 'recipe-switch') {
      if (!wasDragging) {
        gestureType.value = null
        return
      }

      const dx = e.clientX - startX
      const SWIPE_ROUTE_THRESHOLD = 80

      let direction: 'next' | 'prev' | null = null
      if (dx < -SWIPE_ROUTE_THRESHOLD || (velocityX < -SNAP_VELOCITY && dx < -20)) {
        direction = 'next'
      } else if (dx > SWIPE_ROUTE_THRESHOLD || (velocityX > SNAP_VELOCITY && dx > 20)) {
        direction = 'prev'
      }

      if (direction && navigateRecipe(direction)) {
        // Navigating via navigateRecipe
      } else {
        isDragging.value = false
        pageDragOffset.value = 0
        gestureType.value = null
      }
    } else if (currentGestureType === 'settings-switch') {
      if (!wasDragging) {
        gestureType.value = null
        return
      }
      const dx = e.clientX - startX
      const screenW = window.innerWidth || 360
      let deltaIndex = Math.round(-dx / screenW)

      if (deltaIndex === 0 && Math.abs(dx) > 30) {
        if (Math.abs(velocityX) >= SNAP_VELOCITY) {
          deltaIndex = velocityX < 0 ? 1 : -1
        }
      }

      isDragging.value = false
      pageDragOffset.value = 0

      if (deltaIndex !== 0 && route && router) {
        setIgnoreNextClick()
        const tabs = store.settingsAvailableTabs || ['general', 'shoppingList', 'recipes', 'watch', 'data', 'system']
        const currentTab = (route.query.tab as string) || 'general'
        const currentIdx = tabs.indexOf(currentTab)
        if (currentIdx !== -1) {
          const targetIdx = Math.max(0, Math.min(tabs.length - 1, currentIdx + deltaIndex))
          const targetTab = tabs[targetIdx]
          const query = { ...route.query }
          if (targetTab === 'general') {
            delete query.tab
          } else {
            query.tab = targetTab
          }
          router.replace({ path: route.path, query })
        }
      }
    } else if (currentGestureType === 'admin-switch') {
      if (!wasDragging) {
        gestureType.value = null
        return
      }
      const dx = e.clientX - startX
      const screenW = window.innerWidth || 360
      let deltaIndex = Math.round(-dx / screenW)

      if (deltaIndex === 0 && Math.abs(dx) > 30) {
        if (Math.abs(velocityX) >= SNAP_VELOCITY) {
          deltaIndex = velocityX < 0 ? 1 : -1
        }
      }

      isDragging.value = false
      pageDragOffset.value = 0

      if (deltaIndex !== 0) {
        setIgnoreNextClick()
        const tabs = ['feedback', 'stats', 'users', 'apis', 'system']
        const currentTab = (route?.query?.tab as string) || 'feedback'
        const currentIdx = tabs.indexOf(currentTab)
        if (currentIdx !== -1) {
          const targetIdx = Math.max(0, Math.min(tabs.length - 1, currentIdx + deltaIndex))
          const targetTab = tabs[targetIdx]
          if (route && router) {
            const query = { ...route.query }
            if (targetTab === 'feedback') {
              delete query.tab
            } else {
              query.tab = targetTab
            }
            router.replace({ path: route.path, query })
          }
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('admin-switch-tab', { detail: { targetTab, deltaIndex } }))
          }
        }
      }
    } else {
      isDragging.value = false
      pageDragOffset.value = 0
      mainTabProgress.value = null
    }
  }

  const onPointerCancel = (_e: PointerEvent) => {
    if (gestureType.value === 'drawer' && isDragging.value && dragOffset.value !== null) {
      const progress = (dragOffset.value + DRAWER_WIDTH) / DRAWER_WIDTH
      // Browser pointercancel is an interruption (not an explicit finger lift), so only stay open if pulled past 50%
      if (progress >= 0.50) {
        isSidebarOpen.value = true
      }
    }
    if (recipeNavTimer) {
      clearTimeout(recipeNavTimer)
      recipeNavTimer = null
    }
    recipeNavDirection.value = null
    isRecipeNavigating.value = false
    store.activeRecipeIdOverride = null
    tracking          = false
    directionLocked   = false
    directionRejected = true
    isDragging.value  = false
    dragOffset.value  = null
    pageDragOffset.value = 0
    gestureType.value = null
    mainTabProgress.value = null
  }

  const sidebarStyle = computed(() => {
    if (isDesktop.value) return {}
    if (dragOffset.value !== null) {
      return { transform: `translateX(${dragOffset.value}px)` }
    }
    return { transform: isSidebarOpen.value ? 'translateX(0)' : 'translateX(-100%)' }
  })

  const backdropStyle = computed(() => {
    let opacity: number
    if (dragOffset.value !== null) {
      opacity = (dragOffset.value + DRAWER_WIDTH) / DRAWER_WIDTH * 0.55
    } else {
      opacity = isSidebarOpen.value ? 0.55 : 0
    }
    return {
      opacity: String(opacity),
      pointerEvents: (opacity > 0 ? 'auto' : 'none') as 'auto' | 'none',
      transition: dragOffset.value !== null ? 'none' : 'opacity 0.3s ease'
    }
  })

  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value
    dragOffset.value = null
  }

  const closeSidebar = () => {
    isSidebarOpen.value = false
    dragOffset.value = null
  }

  const activeMainTab = computed<'recipes' | 'shopping-list'>(() => {
    if (pendingMainTab.value) return pendingMainTab.value
    if (mainTabProgress.value !== null) {
      return mainTabProgress.value >= 0.5 ? 'shopping-list' : 'recipes'
    }
    return route?.path === '/recipes' ? 'recipes' : 'shopping-list'
  })

  watch(
    () => route?.path,
    () => {
      pendingMainTab.value = null
      dragStartTab.value = null
    }
  )

  return {
    isSidebarOpen,
    isDragging,
    dragOffset,
    pageDragOffset,
    gestureType,
    mainTabProgress,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    sidebarStyle,
    backdropStyle,
    toggleSidebar,
    closeSidebar,
    isDesktop,
    isRecipeNavigating,
    recipeNavDirection,
    navigateRecipe,
    isTabNavigating,
    tabNavDirection,
    tabNavTargetId,
    navigateTab,
    pendingMainTab,
    dragStartTab,
    activeMainTab
  }
}

